/** Cómo se dimensiona el panel respecto a su campo. */
export type DxcPanelWidth = 'field' | 'auto';

/**
 * Anclaje de un panel flotante a su campo.
 *
 * Los tres controles de selección —`dxc-select`, `dxc-multiselect`,
 * `dxc-autocomplete`— abren una lista debajo de un campo, y los tres tenían el
 * mismo defecto: el panel era `position: absolute`, así que lo recortaba
 * cualquier ancestro con `overflow: hidden`. En la vista de borrador eso pasaba
 * dos veces —la tarjeta de contenedor y el propio `.dxc-input-group`— y de una
 * lista de 23 opciones se veían dos.
 *
 * `fixed` saca el panel de esos recortes, a cambio de que las coordenadas sean
 * de viewport y haya que recalcularlas. Eso es lo que hace esto, una vez, en
 * lugar de tres copias.
 *
 * Límite conocido: un ancestro con `transform`, `filter`, `perspective` o
 * `contain` crea un bloque contenedor y `fixed` se ancla a él, no al viewport.
 * En la aplicación solo ocurre con el desenfoque del panel de asistente, que
 * está abierto justo cuando el formulario no es accesible.
 */
export class DxcPanelAnchor {

    /** Estilo en línea para el panel. Vacío mientras está cerrado. */
    style: Record<string, string> = {};
    /** El panel se abrió hacia arriba porque abajo no cabía. */
    up = false;

    #onChange: () => void;
    #reposition = () => this.update();
    #bound = false;
    #observer?: ResizeObserver;
    #frames = 0;

    constructor(
        private host: HTMLElement,
        /** Selector del campo al que se ancla, dentro del host. */
        private fieldSelector: string,
        onChange: () => void,
        /** Alto máximo deseado; se recorta al hueco disponible. */
        private preferred = 280,
        /** Selector del panel. Necesario para medirlo cuando el ancho es `auto`. */
        private panelSelector = '.dxc-combo__panel',
    ) {
        this.#onChange = onChange;
    }

    /**
     * Cómo se dimensiona: pegado al campo, o al ancho de su contenido.
     * Lo fija el componente en cada apertura.
     */
    width: DxcPanelWidth = 'field';
    /** Tope cuando el ancho es `auto`. */
    maxWidth = 520;

    /**
     * Se escucha en captura: el scroll de un contenedor interno no burbujea.
     *
     * Además se recoloca durante unos cuantos fotogramas y se observa el tamaño
     * del campo. La posición se calculaba UNA sola vez al abrir, y cualquier
     * movimiento posterior la dejaba obsoleta: al enfocar, el navegador desplaza
     * el campo hasta hacerlo visible, y el panel se quedaba donde estaba — que
     * es exactamente cómo terminaba tapando su propio campo.
     */
    attach() {
        if (this.#bound) return;
        this.#bound = true;
        window.addEventListener('scroll', this.#reposition, true);
        window.addEventListener('resize', this.#reposition);

        const field = this.host.querySelector(this.fieldSelector);
        if (field && typeof ResizeObserver !== 'undefined') {
            this.#observer = new ResizeObserver(() => this.update());
            this.#observer.observe(field);
        }

        this.#frames = 0;
        this.#settle();
    }

    detach() {
        if (!this.#bound) return;
        this.#bound = false;
        window.removeEventListener('scroll', this.#reposition, true);
        window.removeEventListener('resize', this.#reposition);
        this.#observer?.disconnect();
        this.#observer = undefined;
        this.#frames = 0;
        this.style = {};
    }

    /**
     * Recoloca durante los primeros fotogramas tras abrir. El desplazamiento que
     * provoca el foco no siempre ha terminado cuando se mide la primera vez.
     */
    #settle() {
        if (!this.#bound || this.#frames > 4) return;
        this.#frames++;
        requestAnimationFrame(() => { this.update(); this.#settle(); });
    }

    update() {
        const field = this.host.querySelector(this.fieldSelector);
        if (!field) return;

        const rect = field.getBoundingClientRect();
        const viewportH = window.innerHeight;
        const viewportW = window.innerWidth;
        const gap = 6;
        const margin = 8;
        const below = viewportH - rect.bottom - gap;
        const above = rect.top - gap;

        // Hacia arriba solo si abajo no cabe una lista usable y arriba sí.
        this.up = below < 160 && above > below;
        const room = Math.max(120, Math.floor(this.up ? above : below) - margin);

        const style: Record<string, string> = {
            position: 'fixed',
            maxHeight: `${Math.min(this.preferred, room)}px`,
            ...(this.up
                ? { bottom: `${Math.round(viewportH - rect.top + gap)}px` }
                : { top: `${Math.round(rect.bottom + gap)}px` }),
        };

        if (this.width === 'auto') {
            // Nunca más estrecho que su campo, y como mucho lo que pida el
            // contenido sin salirse de la pantalla. El ancho real lo decide el
            // CSS (`width: max-content`); aquí solo se acotan los extremos.
            const cap = Math.min(this.maxWidth, viewportW - margin * 2);
            style['minWidth'] = `${Math.round(rect.width)}px`;
            style['maxWidth'] = `${Math.round(Math.max(rect.width, cap))}px`;
        } else {
            style['width'] = `${Math.round(rect.width)}px`;
        }

        // Se mide el panel ya pintado para no salirse por la derecha: con ancho
        // automático no se sabe cuánto ocupa hasta que existe.
        const panel = this.host.querySelector(this.panelSelector);
        const panelW = panel ? panel.getBoundingClientRect().width : rect.width;
        const maxLeft = viewportW - margin - panelW;
        style['left'] = `${Math.round(Math.max(margin, Math.min(rect.left, maxLeft)))}px`;

        this.style = style;
        this.#onChange();
    }
}
